import get from 'lodash.get';
var EventBridge = /** @class */ (function () {
    function EventBridge() {
        this.events = this.setEmpty();
        this.triggerBuffer = [];
    }
    /**
     * Binds a callback to the given namespace(s)
     *
     * @remarks
     * If the event is unique and has already fired this will also immediately trigger the callback.
     * Bind could also redefine an existing namespace to be unique which might be undesired.
     *
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for the given namespace(s) if triggered
     * @param isUnique is the event unique? (optional, defaults to false)
     */
    EventBridge.prototype.bind = function (namespaceList, callback, isUnique) {
        var _this = this;
        if (isUnique === void 0) { isUnique = false; }
        var bindOnNamespace = function (namespace) {
            // Can't use tryGotoNamespace here because we don't want to return anything on failure as not reaching the desired namespace is not an error
            var patharr = namespace.split('.');
            var item = _this.events; // Item will be constantly shifted downwards the tree
            // Try to get to the namespace first in the EventSpace Tree
            while (patharr.length !== 0) {
                var head = patharr.shift();
                if (head) {
                    if (!_this.exists(item.children[head])) {
                        // The namespace doesn't exist so it needs to be created
                        var newItem = {};
                        item.children[head] = _this.setEmpty(newItem);
                    }
                    item = item.children[head];
                }
            }
            // Item is now pointed at the correct EventSpace
            item.callbacks.push(callback);
            // This allows bind to redefine an existin namespace as unique
            if (isUnique) {
                item.isUnique = true;
            }
            // If the namespace is unique and has been triggered already we need to execute the callback immediately
            if (item.isUnique && item.hasFired) {
                callback(item.uniqueEventData);
            }
            // If the namespace is new and unique we need to check if somebody tried to trigger it before it was defined
            // In that case we need to trigger now
            if (item.isUnique && !item.hasFired) {
                _this.checkForStoredTrigger(namespace, function (data) {
                    _this.trigger(namespace, data);
                });
            }
            return _this;
        };
        if (namespaceList === '' || typeof namespaceList !== 'string') {
            return this;
        }
        this.forEachNamespace(namespaceList, bindOnNamespace);
        return this;
    };
    /**
     * Alias for `bind` with isUnique set to false
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for the given namespace(s) if triggered
     */
    EventBridge.prototype.on = function (namespace, callback) {
        return this.bind(namespace, callback, false);
    };
    /**
     * Alias for `bind` that will immediately remove the namespace after the first trigger. See `off`.
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for the given namespace(s) if triggered
     */
    EventBridge.prototype.one = function (namespace, callback) {
        var _this = this;
        return this.bind(namespace, function () {
            callback();
            _this.off(namespace);
        }, false);
    };
    /**
     * Alias for `bind` that will immediately remove the namespace after the first trigger. See `off`.
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for the given namespace(s) if triggered
     */
    EventBridge.prototype.unique = function (namespace, callback) {
        return this.bind(namespace, callback, true);
    };
    /**
     * Removes a namespace, its callbacks and all of its children from the EventSpace tree, preventing them from being executed
     *
     * @remarks
     * This doesn't accept a namespace list and will most likely fail if you try to pass one
     *
     * @param namespace The namespace to remove
     */
    EventBridge.prototype.off = function (namespace) {
        var _this = this;
        /**
         * Unbinds the given EventSpace
         * @param item the EventSpace to unbind
         */
        var offSuccess = function (item) {
            // Simply set the EventSpace to empty, this will also remove all of the children from the tree
            _this.setEmpty(item);
            return _this;
        };
        return this.tryGotoNamespace(namespace, offSuccess, function () { return _this; });
    };
    /**
     * Execute all callbacks for the given namespace (this also includes the children) and pass the data to each callback
     * @param namespaceList a space seperated list of namespaces to trigger
     * @param data the payload to pass to each callback
     */
    EventBridge.prototype.trigger = function (namespaceList, data) {
        var _this = this;
        if (namespaceList === '' || typeof namespaceList !== 'string') {
            return this;
        }
        /**
         * Execute all callbacks for the given namespace (this also includes the children) and pass the data to each callback
         * @param item the EventSpace that contains the callbacks we want to execute
         */
        var triggerSuccess = function (item) {
            _this.triggerNamespace(item, data);
            return _this;
        };
        /**
         * Go to the namespace and execute all current triggers or store the trigger for later us if the namespace doesn't exist yet
         * @param space the namespace we want to trigger
         */
        var triggerSpace = function (space) {
            return _this.tryGotoNamespace(space, triggerSuccess, function () {
                _this.storeTrigger(space, data);
                return _this;
            });
        };
        this.forEachNamespace(namespaceList, triggerSpace);
        return this;
    };
    /**
     * Alias for `bind` that will immediately remove the namespace after the first trigger. See `off`.
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for the given namespace(s) if triggered
     */
    EventBridge.prototype.once = function (namespace, callback) {
        return this.one(namespace, callback);
    };
    /**
     * Alias for `trigger`
     * @param namespaceList a space seperated list of namespaces to trigger
     * @param data the payload to pass to each callback
     */
    EventBridge.prototype.emit = function (namespaceList, data) {
        return this.trigger(namespaceList, data);
    };
    /**
     * Get all currently existing event namespaces in a list
     */
    EventBridge.prototype.eventNames = function () {
        var spacesTodo = [];
        var nameSpacesList = [];
        var PREVENT_INFINITE = 1000; // Just for safety
        var runs = 0;
        // We need an entry point which is the root node of the EventSpace tree
        spacesTodo.push({
            path: '',
            spaces: this.events.children,
        });
        while (spacesTodo.length > 0 && runs++ < PREVENT_INFINITE) {
            var current = spacesTodo.shift();
            if (current) {
                // Might not exist if spacesTodo is empty (in which case we are done anyways but eslint cries)
                var spaces = current.spaces;
                for (var space in spaces) {
                    // eslint-disable-next-line no-prototype-builtins
                    if (spaces.hasOwnProperty(space)) {
                        // Build the full namespace path from what we created so far
                        var newPath = current.path === '' ? space : current.path + "." + space;
                        nameSpacesList.push(newPath);
                        var subSpace = spaces[space];
                        // If the subspace has children we add them to the spacesTodo list and move on
                        if (this.exists(subSpace)) {
                            for (var subSpaceChild in subSpace.children) {
                                // eslint-disable-next-line no-prototype-builtins
                                if (subSpace.children.hasOwnProperty(subSpaceChild)) {
                                    spacesTodo.push({
                                        path: newPath,
                                        spaces: subSpace.children,
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
        return nameSpacesList;
    };
    /**
     * Tests if the given thing is defined and not null
     * @param thing the thing to check
     */
    EventBridge.prototype.exists = function (thing) {
        return typeof thing !== 'undefined' && thing !== null;
    };
    /**
     * Sets the given EventSpace to empty or returns a new emtpy one
     *
     * @remarks
     * This will also clean up the children object, effectively removing all child EventSpaces
     *
     * @param item the EventSpace to modify
     */
    EventBridge.prototype.setEmpty = function (item) {
        if (item) {
            item.children = {};
            item.callbacks = [];
            item.isUnique = false;
            item.hasFired = false;
            item.uniqueEventData = null;
            return item;
        }
        return {
            callbacks: [],
            children: {},
            hasFired: false,
            isUnique: false,
            uniqueEventData: null,
        };
    };
    /**
     * Tries to walk down the EventSpace tree to the desired namespace. Executs `success` if the targeted namespace is reached,
     * will call `failure` otherwise
     *
     * @param namespace the namespace to reach
     * @param success the function to call if we reach the namespace
     * @param failure the function to call if we don't find a matching namespace
     */
    EventBridge.prototype.tryGotoNamespace = function (namespace, success, failure) {
        var patharr = namespace.split('.');
        var item = this.events; // Item will be constantly shifted downwards the tree until we reach our destination or run out of valid children to go to
        while (patharr.length !== 0) {
            var head = patharr.shift();
            if (head) {
                if (!this.exists(item.children[head])) {
                    // No child matches the namespace string we try to reach, abort here
                    return failure.call(this, item);
                }
                item = item.children[head];
            }
        }
        return success.call(this, item);
    };
    /**
     * Does the callback for each namespace in the given list of namespaces
     * @param namespaceList a space seperated list of namespaces
     * @param callback the callback to execute for each namespace
     */
    EventBridge.prototype.forEachNamespace = function (namespaceList, callback) {
        namespaceList.split(' ').forEach(callback);
    };
    /**
     * Triggers all callbacks in the EventSpace and all of its children an pass the given data as a payload to each callback
     * @param space the EventSpace to trigger
     * @param data the data to pass to each callback in the EventSpace
     */
    EventBridge.prototype.triggerNamespace = function (space, data) {
        var _this = this;
        var spacesTodo = [];
        var runs = 0;
        var PREVENT_INFINITE = 1000;
        /**
         * Executes the callback function and pass `data` to it
         * @param fun The callback function
         */
        var executeCallback = function (fun) {
            fun(data);
        };
        /**
         * Adds the EventSpace to the queue of spaces that need their callbacks executed
         * @param space The EventSpace
         */
        var addSpaceToQueue = function (space) {
            if (_this.exists(space)) {
                spacesTodo.push(space);
            }
        };
        if (this.exists(space)) {
            spacesTodo.push(space);
            var _loop_1 = function () {
                var current = spacesTodo.pop();
                if (current) {
                    // Might be undefined if spacesTodo is an empty array (which is handled by the while loop but eslint cries)
                    // Unique namespaces need to store if they have been triggered already, all other namespaces don't really care
                    if (current.isUnique && !current.hasFired) {
                        current.hasFired = true;
                    }
                    // Unique events might be bound to after they have already triggered
                    // In this case we will immediately execute the callback on the bind call
                    // So we need to store the payload for that trigger
                    if (current.isUnique && typeof data !== 'undefined') {
                        current.uniqueEventData = data;
                    }
                    current.callbacks.forEach(executeCallback);
                    // All EventSpaces that live under the targeted space also get their callbacks executed
                    if (current.children) {
                        Object.keys(current.children).forEach(function (childkey) {
                            addSpaceToQueue(current.children[childkey]);
                        });
                    }
                }
            };
            // Recursion might be nicer but adds the latent risk of running into a stack overflow so we just loop
            while (spacesTodo.length > 0 && runs++ < PREVENT_INFINITE) {
                _loop_1();
            }
        }
    };
    /**
     * Stores a namespace trigger in the triggerBuffer for it to be executed later on
     * @param namespace the namespace that should be triggered
     * @param data the payload to pass to the callback
     */
    EventBridge.prototype.storeTrigger = function (namespace, data) {
        if (typeof window !== 'undefined') {
            var item = this.triggerBuffer;
            item.push([namespace, data]);
        }
    };
    /**
     * Checks the given namespace for any stored triggers that should be exectuded
     * @param namespace the namespace that might have stored triggers
     * @param onSuccess the callback to execute if the namespace has stored triggers
     */
    EventBridge.prototype.checkForStoredTrigger = function (namespace, onSuccess) {
        var item = this.triggerBuffer;
        item.forEach(function (bufferedTrigger) {
            var bufferedNamespace = bufferedTrigger[0], data = bufferedTrigger[1];
            if (bufferedNamespace === namespace) {
                onSuccess(data);
            }
        });
    };
    return EventBridge;
}());
export { EventBridge };
// The eventBridge might already be defined by another fragment (IS7 or emma) so we use that one if we can and only define a new one if none exists
export var event = typeof window !== 'undefined' && get(window, 'eventBridge')
    ? window.eventBridge
    : new EventBridge();
// No eventBridge has been registred yet so we do it now
if (typeof window !== 'undefined' && typeof window.eventBridge === 'undefined') {
    window.eventBridge = event;
}
//# sourceMappingURL=index.js.map